"use client";

import React, { useMemo, useState } from "react";

import {
  OrganizationMembershipType,
  AcademicRecordType,
} from "../../../../../../types/profiles/memberProfileTypes";
import { CustomUserType } from "../../../../../../types/profiles/authTypes";
import { MemberSpiritualGiftsReport } from "../../../../../../types/profiles/giftTypes";
import { MemberServiceTypeDto } from "../../../../../../types/profiles/memberServicesTypes";
import { compactAcademicPeriod } from "../../../../../../utils/helpers/academicFormat";
import {
  Languages,
  Briefcase,
  Landmark,
  Gift,
  GraduationCap,
  Lock,
} from "lucide-react";

import { Modal, LITApprovedBadge } from "../../../../../common/";
import "./SpiritualServices.css";

interface SpiritualServicesProps {
  organizationMemberships: OrganizationMembershipType[];
  academicRecord?: AcademicRecordType | null;
  customUser: CustomUserType;
  spiritualGifts?: MemberSpiritualGiftsReport | null;
  showGiftsInProfile?: boolean;
  serviceTypes: MemberServiceTypeDto[];
  memberServices: MemberServiceTypeDto[];

  // NEW: pass from profile/serializer
  denominationBranchLabel?: string;
  denominationFamilyLabel?: string | null;
}

const SpiritualServices: React.FC<SpiritualServicesProps> = ({
  organizationMemberships = [],
  academicRecord,
  customUser,
  spiritualGifts,
  showGiftsInProfile = true,
  serviceTypes = [],
  denominationBranchLabel,
  denominationFamilyLabel,
}) => {
  // prefer display fields; fallback to raw value
  const serviceLabel = (svc?: { name_display?: string | null; name?: string | null }) =>
    (svc?.name_display?.trim() ||
      svc?.name?.trim() ||
      "Service");

  // languages chips
  const languages = useMemo(() => {
    const pl = customUser?.primary_language_display ?? customUser?.primary_language ?? "";
    const sl = customUser?.secondary_language_display ?? customUser?.secondary_language ?? "";
    return Array.from(new Set([pl, sl].filter((v) => typeof v === "string" && v.trim().length > 0)));
  }, [
    customUser?.primary_language_display,
    customUser?.secondary_language_display,
    customUser?.primary_language,
    customUser?.secondary_language,
  ]);

  // gifts list (prefer display)
  const giftItems = useMemo(() => {
    const list = spiritualGifts?.gifts ?? [];
    return list.map((g) => {
      const label = g.name_display ?? g.name ?? "";
      const desc = (g.description || "").trim();
      const title = desc ? `${label} — ${desc}` : label;
      return { id: g.id, label, desc, title };
    });
  }, [spiritualGifts]);

  const [openService, setOpenService] = useState<MemberServiceTypeDto | null>(null);

  // member label for fallbacks
  const memberLabel = useMemo(() => {
    const first = (customUser?.name || "").trim();
    const last = (customUser?.family || "").trim();
    const full = [first, last].filter(Boolean).join(" ").trim();
    if (full) return full;

    const username = (customUser?.username || "").trim();
    if (username) return username;

    const email = (customUser?.email || "").trim();
    if (email) {
      const local = email.split("@")[0].split("+")[0].trim();
      if (local) return local;
    }
    return "member";
  }, [customUser?.name, customUser?.family, customUser?.username, customUser?.email]);

  const NP = `Not provided by ${memberLabel}`;

  // small date helpers
  const fmtDate = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : NP);
  const fmtDateOrDash = (d?: string | null) => (d ? new Date(d).toLocaleDateString() : "—");

  // approval text based on sensitivity + status
  const visibleApprovalText = (s: MemberServiceTypeDto) => {
    if (s.service?.is_sensitive) {
      return s.status === "approved" ? "Approved by TownLIT" : "Not approved by TownLIT";
    }
    return "Active (no approval required)";
  };

  // normalize credential host
  const urlHost = (u?: string | null) => {
    if (!u) return NP;
    try {
      return new URL(u).host;
    } catch {
      return NP;
    }
  };

  // ---------------------------------------------------------------------------------------------
  return (
    <section className="ss-container">
      {/* Languages */}
      <div className="ss-section">
        <header className="ss-head">
          <Languages className="ss-ico" size={18} aria-hidden />
          {languages.length ? (
            <ul className="ss-chips" aria-label="Languages">
              {languages.map((lng, i) => (
                <li key={i} className="ss-chip ss-chip-language" title={lng}>
                  {lng}
                </li>
              ))}
            </ul>
          ) : (
            <span className="ss-empty">No languages listed</span>
          )}
        </header>
      </div>

      {/* Denomination */}
      <div className="ss-section">
        <header className="ss-head">
          <Landmark className="ss-ico" size={18} aria-hidden />
          {denominationBranchLabel ? (
            <ul className="ss-chips" aria-label="Denomination">
              <li className="ss-chip" title={denominationBranchLabel}>
                <span className="ss-chip-text">{denominationBranchLabel}</span>
              </li>
              {denominationFamilyLabel ? (
                <li className="ss-chip" title={denominationFamilyLabel}>
                  <span className="ss-chip-text">{denominationFamilyLabel}</span>
                </li>
              ) : null}
            </ul>
          ) : (
            <span className="ss-empty">No denomination provided</span>
          )}
        </header>
      </div>

      {/* Gifts */}
      <div className="ss-section">
        <header className="ss-head">
          <Gift className="ss-ico" size={18} aria-hidden />
        {!showGiftsInProfile && <Lock className="ss-ico-lock" size={16} aria-label="Gifts are private" />}
          {showGiftsInProfile ? (
            giftItems.length ? (
              <ul className="ss-chips" aria-label="Spiritual gifts">
                {giftItems.slice(0, 12).map((g, i) => (
                  <li key={g.id ?? i} className="ss-chip ss-chip-gift" title={g.title} aria-label={g.title}>
                    <span className="ss-chip-text">{g.label}</span>
                  </li>
                ))}
                {giftItems.length > 12 && (
                  <li className="ss-chip ss-chip-more" title={`${giftItems.length - 12} more`}>
                    +{giftItems.length - 12}
                  </li>
                )}
              </ul>
            ) : (
              <span className="ss-empty">No spiritual gifts shared yet</span>
            )
          ) : (
            <span className="ss-empty">Spiritual gifts are private</span>
          )}
        </header>
      </div>

      {/* Service Types */}
      <div className="ss-section">
        <header className="ss-head">
          <Briefcase className="ss-ico" size={18} aria-hidden />
          {serviceTypes.length ? (
            <ul className="ss-chips ss-chips--clickable" aria-label="Service types">
              {serviceTypes.map((st) => {
                // always prefer backend-provided display
                const name = serviceLabel(st?.service);
                const historyDisplay = st?.history?.trim() || "No history provided";
                const isApproved = st.status === "approved";

                return (
                  <li key={st.id}>
                    <button
                      type="button"
                      className={`ss-chip ss-chip-service ${isApproved ? "ss-chip--approved" : ""}`}
                      title={historyDisplay}
                      onClick={() => setOpenService(st)}
                      aria-haspopup="dialog"
                    >
                      {isApproved && <LITApprovedBadge size="sm" className="lit-approved-badge--inline" />}
                      <span className="ss-chip-text">{name}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <span className="ss-empty">Service roles not listed yet</span>
          )}
        </header>
      </div>

      {/* Academic Record */}
      <div className="ss-section">
        <header className="ss-head">
          <GraduationCap className="ss-ico" size={18} aria-hidden />
          {academicRecord ? (
            <div className="ss-row-top">
              <span className="ss-v">
                {[academicRecord.education_document_type, academicRecord.education_degree].filter(Boolean).join(" | ")}
              </span>
            </div>
          ) : (
            <span className="ss-empty">Academic record not provided</span>
          )}
        </header>

        {academicRecord && (
          <div className="ss-academic">
            <div className="ss-row">
              <span className="ss-k">School</span>
              <span className="ss-v">
                {[
                  academicRecord.school,
                  compactAcademicPeriod({
                    status: academicRecord.status,
                    started_at: academicRecord.started_at,
                    expected_graduation_at: academicRecord.expected_graduation_at,
                    graduated_at: academicRecord.graduated_at,
                  }),
                ]
                  .filter(Boolean)
                  .join(" | ")}
              </span>
            </div>

            <div className="ss-row">
              <span className="ss-k">Focus</span>
              <span className="ss-v">{academicRecord.is_theology_related ? "Theology-related" : "General studies"}</span>
            </div>
          </div>
        )}
      </div>

      {/* Organization Memberships */}
      <div className="ss-section">
        <header className="ss-head">
          <Landmark className="ss-ico" size={18} aria-hidden />
          {organizationMemberships.length ? (
            <ul className="ss-chips" aria-label="Organizations">
              {organizationMemberships.map((org, i) => {
                const label = org.org_name || "Organization";
                return (
                  <li key={i} className="ss-chip" title={label}>
                    <span className="ss-chip-text">{label}</span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <span className="ss-empty">Organization memberships not listed yet (Soon)</span>
          )}
        </header>
      </div>

      {/* Service modal */}
      <Modal
        isOpen={!!openService}
        onClose={() => setOpenService(null)}
        // use the same helper for modal title
        title={openService ? `Service: ${serviceLabel(openService.service)}` : "Service Details"}
        cancelText="Close"
        hideFooter
        confirmVariant="primary"
      >
        {openService ? (
          <div
            className={`ss-service-modal ${
              openService?.service?.is_sensitive
                ? openService.status === "approved"
                  ? "is-approved"
                  : "is-not-approved"
                : ""
            }`}
          >
            <div className="ss-row">
              <span className="ss-k">Approval</span>
              <span className="ss-v">{visibleApprovalText(openService)}</span>
            </div>

            <div className="ss-row">
              <span className="ss-k">Issuer</span>
              <span className="ss-v">{openService.credential_issuer || NP}</span>
            </div>
            <div className="ss-row">
              <span className="ss-k">Issued date</span>
              <span className="ss-v">{fmtDate(openService.issued_at)}</span>
            </div>
            <div className="ss-row">
              <span className="ss-k">Expires date</span>
              <span className="ss-v">{fmtDate(openService.expires_at)}</span>
            </div>
            {openService.credential_url !== undefined && (
              <div className="ss-row">
                <span className="ss-k">Credential source</span>
                <span className="ss-v">{urlHost(openService.credential_url)}</span>
              </div>
            )}

            <div className="ss-row">
              <span className="ss-k">Registered on</span>
              <span className="ss-v">{fmtDateOrDash(openService.register_date)}</span>
            </div>
            <div className="ss-row">
              <span className="ss-k">Verified on</span>
              <span className="ss-v">
                {openService.status === "approved" ? fmtDateOrDash(openService.verified_at) : "—"}
              </span>
            </div>

            <div className="ss-history ss-block">
              <h5 className="ss-block-title">Service history:</h5>
              <p className="ss-block-text">{openService.history?.trim() || NP}</p>
            </div>
          </div>
        ) : (
          <div className="ss-report">
            <span className="ss-empty">No details available</span>
          </div>
        )}
      </Modal>
    </section>
  );
};

export default SpiritualServices;
